import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import {
  cacheRows,
  cachedRows,
  describeBeds,
  listBedTypes,
  listRoomClaims,
  type BedType,
  type Guest,
  type Room,
  type RoomClaim,
} from '@dia/db';
import { supabase } from '../../supabase';
import { occupantsOf } from '../logic/rooms';
import { RoomPhoto } from './RoomPhoto';

/**
 * Room cards, Touch Stay style (docs/GUEST-REDESIGN.md Section 5, item 5): a
 * sideways scrolling row, one card per bedroom, each with its photo, its beds
 * and who has it. This reads the claims, it never writes one: taking, moving
 * or releasing a room stays on the Who sleeps where screen (`/cuartos`,
 * `screens/Rooms.tsx`) so there is exactly one place that logic lives.
 */
export function RoomCardRow({ rooms, stayId }: { rooms: Room[]; stayId: string | null }) {
  const { t, lang } = useT();
  const [bedTypes, setBedTypes] = useState<BedType[]>([]);
  const [claims, setClaims] = useState<RoomClaim[] | null>(null);
  const [party, setParty] = useState<Guest[]>([]);

  useEffect(() => {
    let live = true;
    void listBedTypes(supabase).then((rows) => {
      if (live) setBedTypes(rows);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!stayId) {
      setClaims(null);
      setParty([]);
      return;
    }
    let live = true;
    (async () => {
      try {
        const [claimRows, { data, error }] = await Promise.all([
          listRoomClaims(supabase, stayId),
          supabase.from('guests').select('*').eq('stay_id', stayId),
        ]);
        if (error) throw new Error(error.message);
        if (!live) return;
        setClaims(claimRows);
        setParty(await cacheRows(`guests:${stayId}`, (data ?? []) as Guest[]));
      } catch {
        if (!live) return;
        // No signal, or the read failed. Show the room facts and let the
        // guest confirm who has it on Who sleeps where instead of guessing.
        setClaims(null);
        setParty(await cachedRows<Guest>(`guests:${stayId}`));
      }
    })();
    return () => {
      live = false;
    };
  }, [stayId]);

  if (rooms.length === 0) return null;

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4">
      {[...rooms]
        .sort((a, b) => a.sort - b.sort || a.slug.localeCompare(b.slug))
        .map((room) => {
          const roomName = lang === 'en' ? room.name_en : room.name_es;
          const beds = describeBeds(room.beds, bedTypes, lang);
          const here = claims ? occupantsOf(room.id, claims, party, null) : [];
          const takenCount = claims ? claims.filter((c) => c.room_id === room.id).length : 0;

          return (
            <article
              key={room.id}
              className="flex w-56 shrink-0 flex-col gap-2 rounded-xl border border-line bg-surface p-3"
            >
              <RoomPhoto path={room.photo_path} roomName={roomName} />
              <h3 className="font-display text-[1.05em] text-ink">{roomName}</h3>
              <ul className="text-caption text-inkFaint">
                {room.en_suite && <li>{t('guest.rooms.en_suite')}</li>}
                {beds.map((bed, i) => (
                  <li key={`${bed.label}-${i}`}>{t('guest.rooms.bed_line', { count: bed.count, bed: bed.label })}</li>
                ))}
                <li>
                  {room.extra_sleeps > 0
                    ? t('guest.rooms.sleeps', { base: room.base_sleeps, max: room.max_sleeps })
                    : t('guest.rooms.sleeps_plain', { max: room.max_sleeps })}
                </li>
              </ul>

              {claims === null ? (
                <Link to="/cuartos" className="text-caption text-accent underline underline-offset-4 min-h-tap inline-flex items-center">
                  {t('guest.guide.room_sign_in')}
                </Link>
              ) : (
                <p className="text-caption text-inkMuted">
                  {takenCount === 0
                    ? t('guest.guide.room_open')
                    : here[0]?.guest
                      ? t(
                          takenCount > 1 ? 'guest.guide.room_picked_more' : 'guest.guide.room_picked_by',
                          { name: here[0].guest.first_name, count: takenCount - 1 },
                        )
                      : t('guest.guide.room_picked_generic')}
                </p>
              )}

              <Link
                to="/cuartos"
                className="mt-auto min-h-tap inline-flex items-center text-caption text-accent underline underline-offset-4"
              >
                {t('guest.nav.rooms')}
              </Link>
            </article>
          );
        })}
    </div>
  );
}
